'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import cx from 'classnames';

import { NAV_MODAL_ID } from '@pyranger/constants';

import './nav-links.css';

const NavLink = ({
    href,
    label,
    pathname,
}: {
    href: string;
    label: string;
    pathname: string;
}) => (
    <Link
        className={cx(
            'border-b-2 p-2 text-base-content no-underline dark:text-zinc-50',
            {
                'border-b-base-content dark:border-b-zinc-50':
                    pathname.includes(href),
                'border-b-transparent': !pathname.includes(href),
            },
        )}
        href={href}
    >
        {label}
    </Link>
);

/**
 * If the pathname for this link is not currently active, renders a `<NavLink />` component. If
 * the pathname for this link is active, renders a `<button>` that opens a modal containing a list
 * of the `<ArticleCard />`s for this `href`.
 * For the modal implementation, see: libs/ui/src/_client/ArticleCardList/ArticleCardList.tsx
 * @param param
 * @returns
 */
const DrawerLink = ({
    href,
    label,
    pathname,
}: {
    href: string;
    label: string;
    pathname: string;
}) => {
    if (!pathname.includes(href)) {
        return <NavLink href={href} pathname={pathname} label={label} />;
    }

    return (
        <button
            className="border-b-2 border-b-base-content text-base-content dark:border-b-zinc-50 dark:text-zinc-50"
            onClick={() => {
                const dialog = document.querySelector<HTMLDialogElement>(
                    `#${NAV_MODAL_ID}`,
                );

                if (dialog) {
                    dialog.showModal();
                }
            }}
        >
            {label}
        </button>
    );
};

export const NavLinks = ({
    showLessons,
    showProjects,
}: {
    showLessons: boolean;
    showProjects: boolean;
}) => {
    const pathname = usePathname() ?? '';

    return (
        <nav className="navbar-center">
            <menu className="text-sm">
                {/* On smaller screens, we use special `<DrawerLink />`s to open / close the list of
                lessons / projects */}
                <div className="flex gap-6 md:hidden md:gap-16">
                    <NavLink
                        href="/editor"
                        pathname={pathname}
                        label="Editor"
                    />
                    {showLessons && (
                        <DrawerLink
                            href="/lessons"
                            pathname={pathname}
                            label="Lessons"
                        />
                    )}
                    {showProjects && (
                        <DrawerLink
                            href="/projects"
                            pathname={pathname}
                            label="Projects"
                        />
                    )}
                </div>
                {/* On larger screens, we always use `<NavLink>`s. */}
                <div className="hidden gap-6 md:flex md:gap-16">
                    <NavLink
                        href="/editor"
                        pathname={pathname}
                        label="Editor"
                    />
                    {showLessons && (
                        <NavLink
                            href="/lessons"
                            pathname={pathname}
                            label="Lessons"
                        />
                    )}
                    {showProjects && (
                        <NavLink
                            href="/projects"
                            pathname={pathname}
                            label="Projects"
                        />
                    )}
                </div>
            </menu>
        </nav>
    );
};
